// Rules for the Tides panel (tidePanel.js), no DOM: node tests/unit/tidePanel.test.mjs
//
// Everything here is marina wall-clock time: NOAA is asked for `time_zone=lst_ldt` (the
// station's local time), so a prediction "2026-10-26 10:42" is read as day + minutes and
// never passed through a Date, which would shift it on a device in another time zone.

export const WORK_START = 4 * 60 + 30;  // 4:30a
export const WORK_END = 17 * 60 + 30;   // 5:30p

const dayIndex = day => { const [y, m, d] = day.split('-').map(Number); return Date.UTC(y, m - 1, d) / 86400000; };

/** 'YYYY-MM-DD' moved by n days. */
export function shiftDay(day, n) {
  const dt = new Date((dayIndex(day) + n) * 86400000);
  return dt.toISOString().slice(0, 10);
}

/** NOAA hilo predictions ({t: 'YYYY-MM-DD HH:MM', v, type}) → [{day, min, v, type: 'H'|'L', at}], in order. */
export function parseHiLo(predictions) {
  return (predictions || []).map(p => {
    const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})/.exec(p.t || '');
    if (!m) return null;
    const day = m[1], min = Number(m[2]) * 60 + Number(m[3]), v = Number(p.v);
    if (!Number.isFinite(v)) return null;
    const type = /^h/i.test(p.type) ? 'H' : 'L';
    return { day, min, v, type, at: dayIndex(day) * 1440 + min };
  }).filter(Boolean).sort((a, b) => a.at - b.at);
}

/** The day's highs and lows, each tagged with when it falls against working hours. */
export function dayTurns(list, day) {
  return list.filter(e => e.day === day).map(e => ({
    ...e, when: e.min < WORK_START ? 'before' : e.min > WORK_END ? 'after' : 'work',
  }));
}

/** Height (ft) at a wall-clock minute of `day`: cosine between the turning points either side. */
export function heightAt(list, day, min) {
  const at = dayIndex(day) * 1440 + min;
  for (let i = 0; i < list.length - 1; i++) {
    const a = list[i], b = list[i + 1];
    if (at >= a.at && at <= b.at) {
      const frac = b.at === a.at ? 0 : (at - a.at) / (b.at - a.at);
      return a.v + (b.v - a.v) * (1 - Math.cos(Math.PI * frac)) / 2;
    }
  }
  return null;
}

/** [{min, v}] across the whole day, every `step` minutes (gaps where the data doesn't reach). */
export function dayCurve(list, day, step = 10) {
  const out = [];
  for (let min = 0; min <= 1440; min += step) {
    const v = heightAt(list, day, min);
    if (v != null) out.push({ min, v });
  }
  return out;
}

/** The next high or low after `min` on `day` (may be the next day's), or null. */
export function nextTurn(list, day, min) {
  const at = dayIndex(day) * 1440 + min;
  return list.find(e => e.at > at) || null;
}

/** Right now: { height, rising, rate (ft/h), next: {type, v, min, day, inMin} } or null. */
export function tideState(list, day, min) {
  const height = heightAt(list, day, min);
  const next = nextTurn(list, day, min);
  if (height == null || !next) return null;
  const later = heightAt(list, day, min + 30);
  const rate = later == null ? 0 : (later - height) * 2;
  return { height, rising: next.type === 'H', rate, next: { ...next, inMin: next.at - (dayIndex(day) * 1440 + min) } };
}

/** 0 (the day's low) … 1 (the day's high) for `height`; 0.5 with no range. */
export function tideLevel(list, day, height) {
  const vs = list.filter(e => e.day === day).map(e => e.v);
  if (!vs.length || height == null) return 0.5;
  const lo = Math.min(...vs), hi = Math.max(...vs);
  return hi - lo < 0.01 ? 0.5 : Math.max(0, Math.min(1, (height - lo) / (hi - lo)));
}

/** 642 → '10:42 AM'. */
export function timeLabel(min) {
  const h = Math.floor(min / 60) % 24, m = min % 60;
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

/** 100 → '1h 40m', 25 → '25m'. */
export function durationLabel(mins) {
  const h = Math.floor(mins / 60), m = Math.round(mins % 60);
  return h ? `${h}h${m ? ` ${m}m` : ''}` : `${m}m`;
}

export const feet = v => `${v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)} ft`;

/** The marina's { day: 'YYYY-MM-DD', min } right now, in IANA zone `timeZone`. */
export function marinaNow(timeZone, now = new Date()) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  } catch {
    parts = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
  }
  const p = Object.fromEntries(parts.map(x => [x.type, x.value]));
  return { day: `${p.year}-${p.month}-${p.day}`, min: (Number(p.hour) % 24) * 60 + Number(p.minute) };
}
